import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import shareingData from './data/shopingCardData.json';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaFilter, FaTshirt, FaSlack, FaSuitcase, FaRedo } from 'react-icons/fa';
import '../assets/styles/mensStyle.scss';

const Mens = () => {
    const navigate = useNavigate();
    const [mensProducts, setMensProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('All');

    useEffect(() => {
        // Filter only Men's products (usually 'Men' category or specific sub-categories)
        const allProducts = shareingData.shopingCard || [];
        const mensItems = allProducts.filter(item =>
            item.group === "mens" || item.category === "Men" || item.category === "Jeans" // Added Jeans as an example since I saw it in data
        );
        setMensProducts(mensItems);
    }, []);

    const categories = [
        { name: 'All', icon: <FaRedo /> },
        { name: 'Shirts', icon: <FaTshirt /> },
        { name: 'Jeans', icon: <FaSlack /> },
        { name: 'T-Shirts', icon: <FaTshirt /> },
        { name: 'Formal', icon: <FaSuitcase /> },
    ];

    const filteredProducts = activeTab === 'All'
        ? mensProducts
        : mensProducts.filter(item => item.subCategory === activeTab || item.type === activeTab);

    return (
        <div className="mens-page-wrapper">
            {/* Redesigned Luxury Hero Section */}
            <section className="mens-premium-hero">
                <div className="hero-bg-overlay" style={{
                    backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0) 100%), url('/images/mens-hero-right-focus.png')`
                }}></div>
                <Container className="position-relative h-100">
                    <Row className="align-items-center h-100">
                        <Col lg={7} className="hero-content-box animate-fade-in-up">
                            <span className="premium-label mb-3 d-inline-block">PREMIUM COLLECTION 2024</span>
                            <h1 className="hero-main-title mb-4">
                                <span className="d-block text-white">REDEFINING</span>
                                <span className="gradient-text">MODERN ELEGANCE</span>
                            </h1>
                            <p className="hero-subtitle mb-5">
                                Discover our meticulously crafted range of menswear, where timeless tradition meets contemporary sophistication. From tailored suits to essential casuals.
                            </p>
                            <div className="d-flex gap-3">
                                <button className="btn-luxury-primary">Shop Collection</button>
                                <button className="btn-luxury-outline">View Lookbook</button>
                            </div>
                        </Col>
                    </Row>
                </Container>
                <div className="scroll-indicator d-none d-lg-flex">
                    <span>EXPLORE MORE</span>
                    <div className="scroll-line"></div>
                </div>
            </section>

            <section className="mens-category-nav py-4 sticky-top bg-white shadow-sm">
                <Container>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="category-scroll-container">
                            {categories.map((cat, index) => (
                                <button
                                    key={index}
                                    className={`cat-tab-btn ${activeTab === cat.name ? 'active' : ''}`}
                                    onClick={() => setActiveTab(cat.name)}
                                >
                                    <span className="cat-icon">{cat.icon}</span>
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                        <button className="filter-btn-modern">
                            <FaFilter className="me-2" /> Filter
                        </button>
                    </div>
                </Container>
            </section>

            <Container className="py-5">
                {/* Hero Feature Section */}
                <Row className="mb-5 align-items-center luxury-banner-row">
                    <Col lg={6} className="mb-4 mb-lg-0 p-0">
                        <div className="feature-brand-box">
                            <img src="/images/mens-luxury-hero-hd.png" alt="Featured" className="w-100 rounded-4 shadow" />
                            <div className="overlay-content p-5 text-white">
                                <span className="badge bg-warning text-dark mb-3">NEW SEASON</span>
                                <h2 className="display-5 fw-bold mb-3">Urban Essentials</h2>
                                <p className="lead mb-4">Discover the perfect balance between comfort and sophistication.</p>
                                <button className="btn btn-light btn-lg px-4 rounded-pill fw-bold">Shop Now</button>
                            </div>
                        </div>
                    </Col>
                    <Col lg={6} className="ps-lg-5">
                        <div className="collection-header mb-4">
                            <span className="text-primary fw-bold text-uppercase letter-spacing-2">Curated for you</span>
                            <h2 className="title-xl fw-bold mt-1">Refining Masculinity</h2>
                            <p className="text-muted mt-3 fs-5">
                                Our latest collection focuses on sustainable fabrics and modern silhouettes designed for the modern man who values quality.
                            </p>
                        </div>
                        <div className="row g-3">
                            <div className="col-6">
                                <div className="stat-card p-4 rounded-4 bg-white shadow-sm border">
                                    <h4 className="fw-bold mb-1">500+</h4>
                                    <p className="text-muted mb-0 small">Styles Available</p>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="stat-card p-4 rounded-4 bg-white shadow-sm border">
                                    <h4 className="fw-bold mb-1">24h</h4>
                                    <p className="text-muted mb-0 small">Express Shipping</p>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Products Grid */}
                <div className="products-section mt-5">
                    <div className="d-flex justify-content-between align-items-end mb-4">
                        <div>
                            <h3 className="fw-bold mb-1">Featured Styles</h3>
                            <p className="text-muted small mb-0">Showing {filteredProducts.length} results in {activeTab}</p>
                        </div>
                        <a href="#!" className="text-dark fw-bold text-decoration-none">View All <FaArrowRight className="ms-1" /></a>
                    </div>

                    <Row className="g-4">
                        {filteredProducts.slice(0, 12).map((item, index) => (
                            <Col lg={3} md={4} sm={6} key={index}>
                                <div className="mens-product-card shadow-sm rounded-4 overflow-hidden bg-white" onClick={() => navigate(`/product/${item.id}`)}>
                                    <div className="img-wrapper position-relative">
                                        <img src={item.productImage} alt={item.title} className="w-100" />
                                        <div className="card-actions">
                                            <span className="price-badge">${item.price}</span>
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <h6 className="fw-bold mb-1 text-truncate">{item.title}</h6>
                                        <p className="text-muted small mb-2">{item.type || 'Clothing'}</p>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="rating-dots">
                                                <span className="dot bg-primary"></span>
                                                <span className="dot bg-dark"></span>
                                                <span className="dot bg-secondary"></span>
                                            </div>
                                            <button className="add-to-cart-mini">+</button>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </Container>

            {/* Bottom Promo Section */}
            <section className="bottom-promo py-5 bg-dark text-white text-center mt-5">
                <Container>
                    <div className="py-5">
                        <h2 className="display-6 fw-bold mb-4">Join our VIP Club for 20% Off</h2>
                        <div className="mx-auto" style={{ maxWidth: '500px' }}>
                            <div className="input-group mb-3">
                                <input type="text" className="form-control rounded-pill-start p-3 bg-transparent border-secondary text-white" placeholder="Enter your email" />
                                <button className="btn btn-primary rounded-pill-end px-4 fw-bold">Subscribe</button>
                            </div>
                            <p className="text-muted small">Stay updated with latest drops and exclusive offers.</p>
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default Mens;